import PropTypes from "prop-types";
import debounce from "lodash/debounce";
import { useSnackbar } from "notistack";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import Paper from "@mui/material/Paper";
import AddIcon from "@mui/icons-material/Add";
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/Edit";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import TableRow from "@mui/material/TableRow";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import React, { useCallback, useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import { withStyles } from "@mui/styles";
import IconButton from "@mui/material/IconButton";
import TableContainer from "@mui/material/TableContainer";

import UserModal from "./UserModal";
import { styles } from "../../styles/global";
import { Dialog } from "../BaseComponents/Dialog";
import { NoResults } from "../BaseComponents/NoResults";
import { AppSkeleton } from "../BaseComponents/Skeleton";
import { isAdmin, isSukiAdmin } from "../../helpers/roles";
import { createUser, getUsers, deleteUser } from "../../api/user";
import { CREATE_USER_ERROR_CODE, ROLES } from "../../util/constants";
import { filterFullArray, getFullName, stringToHslColor } from "../../util";
import { StyledTableCell, StyledTableRow, ActionCell, UserCell, StyledAvatar } from "./styles";
function Content(props) {
  const { classes, organizationId } = props;

  const [rows, setRows] = React.useState([]);
  const [filteredUsers, setFilteredUsers] = React.useState([]);

  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEmpty, setIsEmpty] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { enqueueSnackbar } = useSnackbar();
  const isUserAdmin = isAdmin();

  const fetchUsers = useCallback(async () => {
    const users = await getUsers({ organizationId });
    setRows(users);
    setFilteredUsers(users);
    setIsEmpty(!users.length);
    setIsLoading(false);
  }, [organizationId]);

  useEffect(() => {
    setIsLoading(true);
    fetchUsers();
  }, [fetchUsers]);

  const handleDeleteUser = async () => {
    await deleteUser({ id: selectedUser.id, organizationId });
    enqueueSnackbar("User deleted successfully.");
    setSelectedUser(null);
    onClose();
    setIsLoading(true);
    fetchUsers();
  };

  const onQueryChange = debounce((query) => {
    const newRows = filterFullArray(query, rows);
    setFilteredUsers(newRows);
    if (!newRows.length) {
      setIsEmpty(true);
    } else if (isEmpty && newRows.length) {
      setIsEmpty(false);
    }
  }, 200);

  const createOrUpdateUser = ({ firstName, middleName, lastName, email, role, id }) => {
    createUser({
      firstName,
      middleName,
      lastName,
      email,
      role,
      organizationId,
      id
    })
      .then(() => {
        setIsLoading(true);
        enqueueSnackbar(`Successfully ${id ? "edited" : "created"} user`);
        fetchUsers();
      })
      .catch((error) => {
        const isUserAlreadyExist = [CREATE_USER_ERROR_CODE.NO_ACCOUNT, CREATE_USER_ERROR_CODE.DUPLICATE_ENTRY].includes(
          error?.code
        );
        if (isUserAlreadyExist) {
          enqueueSnackbar("User already exists", { variant: "error" });
        } else {
          enqueueSnackbar("Something went wrong", { variant: "error" });
        }
        console.error(error);
      });
  };

  const deleteButtonProps = {
    name: "Delete",
    onAction: handleDeleteUser
  };

  const onDelete = (row) => {
    setSelectedUser({
      id: row.id,
      name: getFullName(row.firstName, row.middleName, row.lastName)
    });
    setOpenDeleteDialog(true);
  };

  const onEdit = (row) => {
    setSelectedUser({ ...row, name: getFullName(row.firstName, row.middleName, row.lastName) });
    setOpenUserDialog(true);
  };

  const onClose = () => {
    setOpenUserDialog(false);
    setOpenDeleteDialog(false);
    setSelectedUser(null);
  };

  const isEditForbidden = (role) => !isSukiAdmin() && role === ROLES.SUKI_ADMIN;

  const skeletonWidth = isUserAdmin ? [322, 232, 430] : [322, 702];

  const renderUsers = () => {
    return filteredUsers.map((row, i) => (
      <StyledTableRow key={row.id + i}>
        <StyledTableCell component="th" scope="row">
          <UserCell>
            <StyledAvatar bgcolor={stringToHslColor(row.id)}>
              {row.firstName[0].toUpperCase() + row.lastName[0].toUpperCase()}
            </StyledAvatar>
            <div>
              <div className="name">{getFullName(row.firstName, row.middleName, row.lastName)}</div>
              <div className="email">{row.email}</div>
            </div>
          </UserCell>
        </StyledTableCell>

        <StyledTableCell align="left">{row.id}</StyledTableCell>

        {isUserAdmin && (
          <ActionCell align="center">
            <Tooltip title="Edit">
              <span>
                <IconButton aria-label="edit" onClick={() => onEdit(row)} disabled={isEditForbidden(row.role)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton aria-label="delete" onClick={() => onDelete(row)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </ActionCell>
        )}
      </StyledTableRow>
    ));
  };

  return (
    <>
      <Paper className={classes.paper}>
        <AppBar className={classes.searchBar} position="sticky" color="default" elevation={0}>
          <Toolbar>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <SearchIcon className={classes.block} color="inherit" />
              </Grid>
              <Grid item xs>
                <TextField
                  fullWidth
                  placeholder="Filter users by name or id"
                  InputProps={{
                    className: classes.searchInput
                  }}
                  onKeyUp={(e) => onQueryChange(e.target.value)}
                />
              </Grid>
              <Grid item>
                <Button
                  disabled={!isUserAdmin}
                  variant="contained"
                  color="primary"
                  className={classes.addUser}
                  onClick={() => setOpenUserDialog(true)}
                  startIcon={<AddIcon />}
                >
                  Create New User
                </Button>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
        <div className={classes.contentWrapper}>
          <TableContainer>
            <Table aria-label="customized table" className={classes.table}>
              <TableHead>
                <TableRow>
                  <StyledTableCell align="left">User</StyledTableCell>
                  <StyledTableCell align="left">ID</StyledTableCell>
                  {isUserAdmin && <StyledTableCell align="center">Actions</StyledTableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {!isEmpty && renderUsers()}
                {isLoading && <AppSkeleton widths={skeletonWidth} rowCount={10} />}
              </TableBody>
            </Table>
            {isEmpty && !isLoading && <NoResults />}
          </TableContainer>
        </div>
      </Paper>

      <UserModal
        createOrUpdateUser={createOrUpdateUser}
        selectedUser={selectedUser}
        onClose={onClose}
        open={openUserDialog}
        title={selectedUser ? "Edit User" : "Create New User"}
      />

      <Dialog title="Delete User?" buttonProps={deleteButtonProps} isOpened={openDeleteDialog} onCancel={onClose}>
        {selectedUser?.name}
      </Dialog>
    </>
  );
}

Content.propTypes = {
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(Content);
